import { useEffect, useRef, useState } from 'react';

import { useAuth } from '../contexts/AuthContext';
import { useHouseholdMembership } from '../contexts/HouseholdMembershipContext';
import { useSampleData } from '../contexts/SampleDataContext';
import { getAnalyticsDaily } from '../services/analyticsService';
import {
  getDashboardRefreshGeneration,
  subscribeDashboardRefresh,
} from '../services/dashboardRefreshBus';
import type { AnalyticsDailyPoint } from '../types/analytics';

type Options = {
  year: number;
  /** Category slug, or null / 'all' for total spend. */
  categoryId?: string | null;
  enabled?: boolean;
};

export function useSpendCalendar(options: Options) {
  const { isAuthenticated, tokens } = useAuth();
  const { isSampleMode } = useSampleData();
  const { view: householdView, viewReady } = useHouseholdMembership();
  const enabled = options.enabled !== false;
  const categorySlug =
    options.categoryId && options.categoryId !== 'all' ? options.categoryId : undefined;

  const [days, setDays] = useState<AnalyticsDailyPoint[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const seenGenerationRef = useRef(0);
  const requestSeqRef = useRef(0);
  const requestKey = `${options.year}::${categorySlug ?? 'all'}::${householdView}`;

  const useLive = enabled && isAuthenticated && !isSampleMode && !!tokens?.accessToken;

  useEffect(() => {
    const applyGeneration = (generation: number) => {
      if (generation === seenGenerationRef.current) return;
      seenGenerationRef.current = generation;
      setTick((v) => v + 1);
    };

    applyGeneration(getDashboardRefreshGeneration());
    return subscribeDashboardRefresh(applyGeneration);
  }, []);

  useEffect(() => {
    if (!useLive || !tokens?.accessToken || !viewReady) {
      if (!useLive || !tokens?.accessToken) {
        setDays([]);
        setError(null);
        setLoading(false);
      }
      return;
    }

    const seq = ++requestSeqRef.current;
    let cancelled = false;
    setDays([]);
    setLoading(true);
    setError(null);

    void getAnalyticsDaily({
      accessToken: tokens.accessToken,
      year: options.year,
      tab: 'spend',
      categorySlug,
      view: householdView,
      // Always bust caches when year/category/tick changes.
      forceRefresh: true,
    })
      .then((response) => {
        if (cancelled || seq !== requestSeqRef.current) return;
        setDays(response.days);
        setLoading(false);
      })
      .catch((err: Error) => {
        if (cancelled || seq !== requestSeqRef.current) return;
        setError(err.message);
        setDays([]);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [useLive, tokens?.accessToken, options.year, categorySlug, requestKey, tick, householdView, viewReady]);

  const totalSpend = days.reduce((sum, d) => sum + d.amount, 0);

  return {
    days,
    totalSpend,
    loading,
    error,
    isLive: useLive,
    requestKey,
    refresh: () => setTick((v) => v + 1),
  };
}
